// ============================================================================
// MentionService.js
// ----------------------------------------------------------------------------
// Llamadas a la API de la feature de menciones @usuario (contrato §4):
//   - mentionable_users          (GET)  → autocompletado del editor
//   - my_notifications           (GET)  → menciones in-app de la campana
//   - notification_mark_read     (POST) → marcar UNA mención leída
//   - notification_mark_all_read (POST) → marcar TODAS las menciones leídas
//
// Convención de respuesta del contrato: { success, data, message? }. Los
// endpoints actuales del backend devuelven { status:'success', data }, así que
// aceptamos AMBAS formas para no romper cuando el backend aterrice.
//
// Caché: la lista de mentionable_users se cachea en memoria (no cambia entre
// comentarios). Se invalida sola al recargar la app o con clearUsersCache().
//
// Mock de desarrollo (§7): si el endpoint no existe (404) o falla y el flag
// MENTIONS_DEV_MOCK está activo, se devuelven datos de ejemplo para revisar la
// UX en local. En producción el flag es false → nunca se usa el mock.
// ============================================================================

import apiClient from './apiClient';
import {
  MENTIONS_DEV_MOCK,
  MOCK_MENTIONABLE_USERS,
  MOCK_MY_NOTIFICATIONS
} from '../utils/mentionDevMock';

class MentionService {
  constructor() {
    this._usersCache = null;       // array de usuarios o null si no cargado
    this._usersPromise = null;     // promesa en vuelo (evita peticiones duplicadas)
  }

  // Normaliza { success } y { status:'success' } a un único shape.
  _unwrap(result) {
    const ok = result?.success === true || result?.status === 'success';
    return { ok, data: result?.data, raw: result };
  }

  // ==========================================================================
  // 4.1 mentionable_users — lista de usuarios del tenant para el autocompletado
  // ==========================================================================

  /**
   * Devuelve (y cachea) la lista de usuarios mencionables del tenant.
   * Si ya hay una petición en vuelo, la reutiliza.
   */
  async getMentionableUsers({ force = false } = {}) {
    if (!force && Array.isArray(this._usersCache)) {
      return this._usersCache;
    }
    if (!force && this._usersPromise) {
      return this._usersPromise;
    }

    this._usersPromise = this._fetchMentionableUsers()
      .then((users) => {
        this._usersCache = users;
        this._usersPromise = null;
        return users;
      })
      .catch((err) => {
        this._usersPromise = null;
        throw err;
      });

    return this._usersPromise;
  }

  async _fetchMentionableUsers() {
    try {
      // 404/501 (endpoint aún no existe) → apiClient lanza y cae en el catch,
      // donde el mock de dev cubre el mismo caso que antes.
      const { ok, data } = this._unwrap(await apiClient.get('mentionable_users'));
      if (ok && Array.isArray(data)) return data;

      if (MENTIONS_DEV_MOCK) return [...MOCK_MENTIONABLE_USERS];
      return [];
    } catch (error) {
      if (MENTIONS_DEV_MOCK) return [...MOCK_MENTIONABLE_USERS];
      console.error('MentionService.mentionable_users:', error);
      return [];
    }
  }

  clearUsersCache() {
    this._usersCache = null;
    this._usersPromise = null;
  }

  // ==========================================================================
  // 4.2 my_notifications — menciones in-app del usuario (campana)
  // ==========================================================================

  /**
   * Devuelve { notifications: [...], unreadCount: N }.
   * Si el endpoint no existe y el mock está activo, devuelve datos de ejemplo
   * que emulan EL MISMO shape del backend ({ data:{ items, unread_count } }).
   */
  async getMyNotifications({ limit = 20 } = {}) {
    try {
      const result = await apiClient.get('my_notifications', { limit });
      const parsed = this._parseMyNotifications(result);
      if (parsed) return parsed;

      if (MENTIONS_DEV_MOCK) return this._parseMyNotifications(this._mockRawResponse());
      return { notifications: [], unreadCount: 0 };
    } catch (error) {
      if (MENTIONS_DEV_MOCK) return this._parseMyNotifications(this._mockRawResponse());
      console.error('MentionService.my_notifications:', error);
      return { notifications: [], unreadCount: 0 };
    }
  }

  // Parsea la respuesta cruda de my_notifications al shape interno
  // { notifications, unreadCount }. Devuelve null si la respuesta no es de éxito.
  // El backend (confirmado) envuelve todo en sendResponse(), que solo admite UN
  // payload, así que devuelve data = { items:[...], unread_count:N }. Aceptamos
  // también data como array directo por robustez (forma del §4.2 del contrato).
  _parseMyNotifications(result) {
    const { ok, data } = this._unwrap(result);
    if (!ok || !data) return null;

    const notifications = Array.isArray(data)
      ? data
      : Array.isArray(data.items)
        ? data.items
        : [];

    const unreadCount =
      typeof data.unread_count === 'number'
        ? data.unread_count
        : typeof result.unread_count === 'number'
          ? result.unread_count
          : notifications.filter((n) => !n.leido).length;

    return { notifications, unreadCount };
  }

  // Respuesta CRUDA de ejemplo, idéntica en forma a la del backend real:
  // { success:true, data:{ items:[...], unread_count:N } }. Así el mock recorre
  // exactamente el mismo parser que el endpoint de producción.
  _mockRawResponse() {
    const items = MOCK_MY_NOTIFICATIONS.map((n) => ({ ...n }));
    const unread_count = items.filter((n) => !n.leido).length;
    return { success: true, data: { items, unread_count } };
  }

  // ==========================================================================
  // 4.3 notification_mark_read — marca UNA mención leída
  // ==========================================================================

  async markRead(id) {
    if (id == null) return { success: false };
    try {
      const { ok } = this._unwrap(await apiClient.post('notification_mark_read', { id }));
      return { success: ok };
    } catch (error) {
      // En dev con mock, fingimos éxito para que la UI reaccione.
      if (MENTIONS_DEV_MOCK) return { success: true, mocked: true };
      console.error('MentionService.notification_mark_read:', error);
      return { success: false };
    }
  }

  // ==========================================================================
  // 4.4 notification_mark_all_read — marca TODAS las menciones del usuario
  // ==========================================================================

  async markAllRead() {
    try {
      const { ok } = this._unwrap(await apiClient.post('notification_mark_all_read'));
      return { success: ok };
    } catch (error) {
      if (MENTIONS_DEV_MOCK) return { success: true, mocked: true };
      console.error('MentionService.notification_mark_all_read:', error);
      return { success: false };
    }
  }

  // ==========================================================================
  // notification_delete / notification_delete_all — borrado real de la fila
  // ==========================================================================

  async deleteNotification(id) {
    if (id == null) return { success: false };
    try {
      const { ok } = this._unwrap(await apiClient.post('notification_delete', { id }));
      return { success: ok };
    } catch (error) {
      if (MENTIONS_DEV_MOCK) return { success: true, mocked: true };
      console.error('MentionService.notification_delete:', error);
      return { success: false };
    }
  }

  async deleteAllNotifications() {
    try {
      const { ok } = this._unwrap(await apiClient.post('notification_delete_all'));
      return { success: ok };
    } catch (error) {
      if (MENTIONS_DEV_MOCK) return { success: true, mocked: true };
      console.error('MentionService.notification_delete_all:', error);
      return { success: false };
    }
  }
}

const mentionService = new MentionService();
export default mentionService;
